// components/VerificationTrends.tsx
'use client';

import React, { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  Area,
  AreaChart,
} from 'recharts';
import { getVerificationStatistics } from '../app/api/verificationApi';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

interface VerificationTrendsProps {
  filter: string;
  dateRange: string;
  municipality?: string;
  brand?: string;
  year?: string;
}

export default function VerificationTrends({ 
  filter, 
  dateRange, 
  municipality, 
  brand, 
  year 
}: VerificationTrendsProps) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any[]>([]);
  const [selectedView, setSelectedView] = useState<'area' | 'bar'>('area');

  useEffect(() => {
    fetchTrends();
  }, [filter, dateRange, municipality, brand, year]);

  const fetchTrends = async () => {
    try {
      setLoading(true);
      const response: any = await getVerificationStatistics(filter, dateRange || undefined);
      
      if (response.success) {
        const stats = response.statistics;
        let chartData = [];

        // Get data based on filter
        if (filter === 'day' && dateRange) {
          chartData = stats.byDay || [];
        } else if (filter === 'week' && dateRange) {
          chartData = (stats.byDay || []).slice(0, 7);
        } else if (filter === 'month' && dateRange) {
          chartData = stats.byDay || [];
        } else if (filter === 'year' && dateRange) {
          chartData = stats.byMonth || [];
        } else {
          chartData = (stats.byDay || []).slice(0, 30);
        }

        // Apply additional filters (municipality, brand, year) if needed
        // This would require additional API calls or client-side filtering
        // For now, we'll use the data as is

        const formattedData = chartData.map((item: any) => {
          let label = '';
          if (item.date) {
            const date = parseISO(item.date);
            if (filter === 'year') {
              label = format(date, 'MMM yyyy', { locale: fr });
            } else if (filter === 'month' || filter === 'week') {
              label = format(date, 'd MMM', { locale: fr });
            } else {
              label = format(date, 'd MMM', { locale: fr });
            }
          } else if (item.year && item.month) {
            label = `${item.month}/${item.year}`;
          } else if (item.year && item.week) {
            label = `S${item.week} ${item.year}`;
          } else {
            label = 'N/A';
          }

          return {
            label,
            count: item.count || 0,
            date: item.date || item.month_start || item.week_start,
            fullDate: item.date,
          };
        });

        setData(formattedData.reverse());
      }
    } catch (error) {
      console.error('Error fetching trends:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-slate-200 rounded w-1/3 mb-4"></div>
          <div className="h-64 bg-slate-100 rounded"></div>
        </div>
      </div>
    );
  }

  const total = data.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-xl font-semibold text-slate-900">Tendance des Enregistrements</h3>
          <p className="mt-1 text-sm text-slate-500">
            Total: <span className="font-semibold text-slate-700">{total.toLocaleString()}</span> véhicules enregistrés
            {(municipality || brand || year) && (
              <span className="ml-2 text-xs text-slate-400">
                {municipality && `• ${municipality}`}
                {brand && `• ${brand}`}
                {year && `• ${year}`}
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2 bg-slate-50 rounded-xl border border-slate-200 p-1">
          <button
            onClick={() => setSelectedView('area')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              selectedView === 'area'
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Aire
          </button>
          <button
            onClick={() => setSelectedView('bar')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              selectedView === 'bar'
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Barres
          </button>
        </div>
      </div>

      <div className="mt-5 h-64">
        <ResponsiveContainer width="100%" height="100%">
          {selectedView === 'area' ? (
            <AreaChart data={data}>
              <defs>
                <linearGradient id="trendGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12, fill: '#94A3B8' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 12, fill: '#94A3B8' }}
                tickLine={false}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
                labelStyle={{ fontWeight: 'bold', color: '#1E293B' }}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="count"
                stroke="#3B82F6"
                strokeWidth={2}
                fill="url(#trendGradient)"
                name="Véhicules enregistrés"
              />
            </AreaChart>
          ) : (
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12, fill: '#94A3B8' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 12, fill: '#94A3B8' }}
                tickLine={false}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
                labelStyle={{ fontWeight: 'bold', color: '#1E293B' }}
              />
              <Legend />
              <Bar
                dataKey="count"
                fill="#3B82F6"
                radius={[4, 4, 0, 0]}
                name="Véhicules enregistrés"
              />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}